import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

const VIEWPORT_MARGIN = 16;
const TARGET_GAP = 12;
const FALLBACK_TOOLTIP_SIZE = { width: 360, height: 280 };

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

/**
 * Keep the tour card fully on-screen. Left-side targets (sidebar icons,
 * including Edit Profile) open to the right so the rail never needs to expand.
 * Right-side targets (notifications) open to the left so a wide card is not clipped.
 */
export function placeTooltip(target, size, viewport, options = {}) {
  const margin = options.margin ?? VIEWPORT_MARGIN;
  const gap = options.gap ?? TARGET_GAP;
  const vw = viewport.width;
  const vh = viewport.height;
  const tw = Math.min(size.width, Math.max(0, vw - margin * 2));
  const th = Math.min(size.height, Math.max(0, vh - margin * 2));

  if (!target) {
    return {
      top: Math.max(margin, (vh - th) / 2),
      left: Math.max(margin, (vw - tw) / 2),
    };
  }

  const spaceRight = vw - (target.left + target.width) - margin;
  const spaceLeft = target.left - margin;
  const spaceBelow = vh - (target.top + target.height) - margin;
  const spaceAbove = target.top - margin;
  const preferRight = target.left + target.width / 2 < vw / 2;

  const fitsRight = spaceRight >= tw + gap;
  const fitsLeft = spaceLeft >= tw + gap;
  const fitsBelow = spaceBelow >= th + gap;
  const fitsAbove = spaceAbove >= th + gap;

  let top;
  let left;

  if (preferRight && fitsRight) {
    left = target.left + target.width + gap;
    top = target.top + (target.height - th) / 2;
  } else if (!preferRight && fitsLeft) {
    left = target.left - gap - tw;
    top = target.top + (target.height - th) / 2;
  } else if (fitsBelow) {
    top = target.top + target.height + gap;
    left = target.left + target.width / 2 - tw / 2;
  } else if (fitsAbove) {
    top = target.top - gap - th;
    left = target.left + target.width / 2 - tw / 2;
  } else if (fitsRight) {
    left = target.left + target.width + gap;
    top = target.top + (target.height - th) / 2;
  } else if (fitsLeft) {
    left = target.left - gap - tw;
    top = target.top + (target.height - th) / 2;
  } else {
    top = target.top + target.height + gap;
    left = target.left + target.width / 2 - tw / 2;
  }

  return {
    top: clamp(top, margin, Math.max(margin, vh - th - margin)),
    left: clamp(left, margin, Math.max(margin, vw - tw - margin)),
  };
}

export const WALKTHROUGH_STEPS = [
  {
    id: 'dashboard',
    title: 'Welcome to your Liaizn Dashboard',
    body: 'This is where you can track your progress and see what you should do next.',
  },
  {
    id: 'profile',
    title: 'Edit Profile',
    body: 'Add your education, skills, resume and experience to create your employability profile.',
  },
  {
    id: 'prescreening',
    title: 'Complete Prescreening',
    body: 'Answer a short background questionnaire. Skill assessments stay locked until this is done.',
  },
  {
    id: 'assessments',
    title: 'Assess Your Skills',
    body: 'Take assessments to understand your current strengths and areas that need improvement.',
  },
  {
    id: 'courses',
    title: 'Improve Your Skills',
    body: 'Explore learning recommendations based on your profile and assessment results.',
  },
  {
    id: 'jobs',
    title: 'Explore Career Opportunities',
    body: 'Discover opportunities that may match your skills and career interests.',
  },
  {
    id: 'reports',
    title: 'Review Your Reports',
    body: 'Open your reports to see strengths, gaps, and what to work on next.',
  },
];

const FEEDBACK_OPTIONS = [
  { id: 'yes', label: 'Yes' },
  { id: 'somewhat', label: 'Somewhat' },
  { id: 'no', label: 'No' },
];

function measureTarget(stepId) {
  if (typeof document === 'undefined') return null;
  const el = document.querySelector(`[data-tour="${stepId}"]`);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  if (!rect.width && !rect.height) return null;
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
}

const ProductWalkthrough = ({ open, assessmentsLocked, onComplete, showFeedback = true }) => {
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState('steps');
  const [dontShow, setDontShow] = useState(false);
  const [target, setTarget] = useState(null);
  const tooltipRef = useRef(null);
  const [tooltipSize, setTooltipSize] = useState(FALLBACK_TOOLTIP_SIZE);
  const step = WALKTHROUGH_STEPS[index];
  const isLast = index === WALKTHROUGH_STEPS.length - 1;
  const lockedNote = useMemo(
    () => (step?.id === 'assessments' && assessmentsLocked
      ? 'Assessments stay locked until you complete resume upload and prescreening.'
      : null),
    [step, assessmentsLocked]
  );

  useEffect(() => {
    if (!open) {
      setIndex(0);
      setPhase('steps');
      setDontShow(false);
      setTarget(null);
      return undefined;
    }
    const update = () => setTarget(measureTarget(step?.id));
    update();
    const frame = window.requestAnimationFrame(update);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, index, step?.id, phase]);

  useLayoutEffect(() => {
    if (!open || !tooltipRef.current) return undefined;
    const node = tooltipRef.current;
    const measure = () => {
      const rect = node.getBoundingClientRect();
      const next = { width: rect.width, height: rect.height };
      if (next.width === 0 && next.height === 0) return;
      setTooltipSize((previous) => (
        previous.width === next.width && previous.height === next.height ? previous : next
      ));
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (observer) observer.observe(node);
    return () => {
      if (observer) observer.disconnect();
    };
  }, [open, index, phase, lockedNote, showFeedback, target]);

  if (!open) return null;

  const finishToFeedback = (hideForever) => {
    if (!showFeedback) {
      if (onComplete) onComplete({ dontShow: Boolean(hideForever), feedback: null });
      return;
    }
    setDontShow(Boolean(hideForever));
    setPhase('feedback');
  };

  const submitFeedback = (feedback) => {
    if (onComplete) {
      onComplete({ dontShow, feedback });
    }
  };

  const tooltipStyle = phase === 'feedback'
    ? { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
    : placeTooltip(
      target,
      tooltipSize,
      { width: window.innerWidth, height: window.innerHeight },
    );

  return (
    <div className="fixed inset-0 z-[70]">
      <div className="absolute inset-0 bg-black/40" />
      {phase === 'steps' && target && (
        <div
          className="absolute z-[80] rounded-md ring-2 ring-primary-400 ring-offset-2 ring-offset-black/40 pointer-events-none"
          style={{
            top: target.top - 4,
            left: target.left - 4,
            width: target.width + 8,
            height: target.height + 8,
          }}
        />
      )}
      <div
        ref={tooltipRef}
        className="absolute z-[90] bg-white rounded-xl shadow-xl w-[min(22rem,calc(100%-2rem))] max-h-[calc(100vh-2rem)] overflow-y-auto p-4"
        style={tooltipStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="walkthrough-title"
      >
        {phase === 'steps' ? (
          <>
            <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
              Step {index + 1} of {WALKTHROUGH_STEPS.length}
            </p>
            <h3 id="walkthrough-title" className="text-xl font-semibold text-gray-900 mt-2">{step.title}</h3>
            <p className="text-sm text-gray-600 mt-2">{step.body}</p>
            {lockedNote && <p className="text-sm text-amber-700 mt-2">{lockedNote}</p>}
            <div className="flex flex-wrap items-center justify-between gap-2 mt-4">
              {showFeedback ? (
                <button
                  type="button"
                  onClick={() => finishToFeedback(true)}
                  className="btn-ghost"
                >
                  Don&apos;t show again
                </button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => finishToFeedback(false)}
                  className="btn-secondary"
                >
                  Skip
                </button>
                <button
                  type="button"
                  disabled={index === 0}
                  onClick={() => setIndex((value) => Math.max(0, value - 1))}
                  className="btn-secondary"
                >
                  Previous
                </button>
                {isLast ? (
                  <button
                    type="button"
                    onClick={() => finishToFeedback(false)}
                    className="btn-primary"
                  >
                    Finish
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIndex((value) => Math.min(WALKTHROUGH_STEPS.length - 1, value + 1))}
                    className="btn-primary"
                  >
                    Next
                  </button>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <h3 id="walkthrough-title" className="text-xl font-semibold text-gray-900">
              After the walkthrough, do you understand what Liaizn can help you with?
            </h3>
            <p className="text-sm text-gray-600 mt-2">Your answer helps us improve the first-time experience.</p>
            <div className="flex flex-wrap gap-2 mt-6">
              {FEEDBACK_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => submitFeedback(option.id)}
                  className="btn-primary"
                >
                  {option.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductWalkthrough;
